import { baseApi } from '../baseApi';

import { GameMenuItem } from './GameMenuItem';
import { MenuItem } from './MenuItem';
import { MenuItemCategory } from './MenuItemCategory';
import { MenuItemCondiment } from './MenuItemCondiment';
import { MenuVersion } from './MenuVersion';
import { UnitOfSale } from './UnitOfSale';

const menuSlice = baseApi
  .enhanceEndpoints({
    addTagTypes: ['MenuItems', 'MenuItemCategories', 'UnitOfSales', 'MenuItemCondiments', 'MenuVersion', 'GameMenuItems', 'Transactions'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getMenuItems: builder.query<MenuItem[], void>({
        keepUnusedDataFor: 300,
        query: () => ({
          url: '/MenuItems',
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'MenuItems' as const,
                  id,
                })),
                { type: 'MenuItems', id: 'LIST' },
              ]
            : [{ type: 'MenuItems', id: 'LIST' }],
      }),
      getMenuCurrentVersion: builder.query<MenuVersion, void>({
        keepUnusedDataFor: 300,
        query: () => ({
          url: '/MenuItems/CurrentVersion',
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result) => (result ? [{ type: 'MenuVersion', id: result.versionNumber }] : []),
      }),
      getMenuCategories: builder.query<MenuItemCategory[], void>({
        keepUnusedDataFor: 300,
        query: () => ({
          url: '/MenuItems/GetCategories',
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'MenuItemCategories' as const,
                  id,
                })),
                { type: 'MenuItemCategories', id: 'LIST' },
              ]
            : [{ type: 'MenuItemCategories', id: 'LIST' }],
      }),
      getUnitOfSales: builder.query<UnitOfSale[], void>({
        keepUnusedDataFor: 300,
        query: () => ({
          url: '/MenuItems/GetUnitsOfSale',
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'UnitOfSales' as const,
                  id,
                })),
                { type: 'UnitOfSales', id: 'LIST' },
              ]
            : [{ type: 'UnitOfSales', id: 'LIST' }],
      }),
      getCondiments: builder.query<MenuItemCondiment[], void>({
        keepUnusedDataFor: 300,
        query: () => ({
          url: '/MenuItems/Condiments',
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'MenuItemCondiments' as const,
                  id,
                })),
                { type: 'MenuItemCondiments', id: 'LIST' },
              ]
            : [{ type: 'MenuItemCondiments', id: 'LIST' }],
      }),
      getGameMenuItem: builder.query<GameMenuItem, number>({
        keepUnusedDataFor: 0,
        query: (menuItemId) => ({
          url: `/MenuItems/Games/${menuItemId}`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        providesTags: (result, error, menuItemId) => [{ type: 'GameMenuItems', id: menuItemId }],
      }),
      addGameToTransaction: builder.mutation<
        void,
        {
          transactionId: number;
          menuItemId: number;
          unitOfSaleId: number;
          gameId: number;
          instanceId: number;
          quantity: number;
          startDateTime: string;
          customerName: string;
        }
      >({
        query: (body) => ({
          url: '/MenuItems/Games/add-to-transaction',
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
          body,
        }),
        invalidatesTags: ['Transactions'],
      }),
      getGameLineItemStatus: builder.query<
        {
          bookingId: number;
          startDateTime: string;
          endDateTime: string;
          isActive: boolean;
          isCompleted: boolean;
          elapsedMinutes: number;
        },
        number
      >({
        query: (lineItemId) => ({
          url: `/MenuItems/Games/line-items/${lineItemId}/status`,
          method: 'GET',
          headers: {
            'x-api-version': '1.0',
          },
        }),
      }),
      endGameLineItem: builder.mutation<{ lineItemId: number; actualMinutesUsed: number; transactionId: number }, number>({
        query: (lineItemId) => ({
          url: `/MenuItems/Games/line-items/${lineItemId}/end-game`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
        }),
        invalidatesTags: ['Transactions'],
      }),
      moveGameLineItem: builder.mutation<
        {
          success: boolean;
          errorMessage?: string;
          bookingId: number;
          fromInstanceId: number;
          toInstanceId: number;
          totalElapsedMinutes: number;
          instanceUsages: {
            instanceId: number;
            instanceName: string;
            startedAt: string;
            endedAt: string | null;
            minutes: number;
          }[];
        },
        { lineItemId: number; newInstanceId: number }
      >({
        query: ({ lineItemId, newInstanceId }) => ({
          url: `/MenuItems/Games/line-items/${lineItemId}/move`,
          method: 'POST',
          headers: {
            'x-api-version': '1.0',
          },
          body: { newInstanceId },
        }),
        invalidatesTags: ['Transactions'],
      }),
    }),
  });

export const {
  useLazyGetMenuItemsQuery,
  useGetMenuItemsQuery,
  useGetCondimentsQuery,
  useGetMenuCategoriesQuery,
  useGetUnitOfSalesQuery,
  useLazyGetCondimentsQuery,
  useLazyGetMenuCategoriesQuery,
  useLazyGetUnitOfSalesQuery,
  useGetMenuCurrentVersionQuery,
  useLazyGetMenuCurrentVersionQuery,
  useGetGameMenuItemQuery,
  useLazyGetGameMenuItemQuery,
  useAddGameToTransactionMutation,
  useLazyGetGameLineItemStatusQuery,
  useEndGameLineItemMutation,
  useMoveGameLineItemMutation,
} = menuSlice;
